import { boltApi } from 'store/queries/bolt-api'
import { Contact, Opportunity, Referral } from 'types/contacts.ts'
import {
  CreateOpportunityCasePayload,
  GetContactReferralsResponse,
  GetContactResponse,
  GetMyEventsResponse,
  SendSurveyClosedPayload,
  UpdateAccountPayload,
  UpdateAccountResponse,
  UpdateSalesContactPayload,
  UpdateSalesOpportunityPayload,
  UpdateSalesOpportunityResponse
} from './types.ts'
import { TagTypes } from 'store/queries/bolt-api/types.ts'
import { Methods, Response } from 'types/request.ts'
import { Flow } from 'types/logging.ts'
import { EventLogType } from 'types/eventLog.ts'
import { stringify } from 'qs'
import { createErrorTransformer } from 'store/queries/utils.ts'

export const contactsBoltApi = boltApi.injectEndpoints({
  endpoints: (builder) => ({
    // QUERIES
    // -------

    getMyEvents: builder.query<GetMyEventsResponse['events'], EventLogType[] | undefined>({
      providesTags: [TagTypes.CONTACT, TagTypes.EVENT],
      query: (eventTypes) => {
        const query = stringify({ type: eventTypes }, { skipNulls: true, addQueryPrefix: true })
        return {
          url: `/contacts/me/events${query}`
        }
      },
      transformResponse: ({ success, data }: Response<GetMyEventsResponse>) => {
        return success && data?.events ? data.events : []
      }
    }),

    // GET CONTACT
    getContact: builder.query<Contact | null, string>({
      providesTags: [TagTypes.CONTACT, TagTypes.ACCOUNT, TagTypes.OPPORTUNITY],
      query: (contactId) => ({
        url: `/contacts/${contactId}`
      }),
      transformResponse: ({ success, data }: Response<GetContactResponse>) => {
        return success && data ? data.contact : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:getContact]`)
    }),

    // GET CONTACT REFERRALS
    getContactReferrals: builder.query<Referral[], string>({
      query: (contactId) => ({
        url: `/contacts/${contactId}/referrals`
      }),
      transformResponse: ({ success, data }: Response<GetContactReferralsResponse>) => {
        return success && data ? data.referrals : []
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:getContactReferrals]`)
    }),

    // MUTATIONS
    // ---------

    // UPDATE OPPORTUNITY
    updateOpportunity: builder.mutation<Opportunity | null, UpdateSalesOpportunityPayload>({
      invalidatesTags: [TagTypes.OPPORTUNITY],
      query: ({ accountId, contactId, opportunity }) => ({
        method: Methods.PATCH,
        url: `/contacts/${contactId}/accounts/${accountId}/opportunities/${opportunity.sfId}`,
        body: { opportunity }
      }),
      transformResponse: ({ success, data }: Response<UpdateSalesOpportunityResponse>) => {
        return success && data ? data?.opportunity : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:updateOpportunity]`)
    }),

    // UPDATE SALES CONTACT
    updateSalesContact: builder.mutation<Contact | null, UpdateSalesContactPayload>({
      invalidatesTags: [TagTypes.CONTACT],
      query: ({ contact }) => ({
        method: Methods.PATCH,
        url: `/contacts/${contact.sfId}`,
        body: { contact }
      }),
      transformResponse: ({ success, data }: Response<GetContactResponse>) => {
        return success && data ? data?.contact : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:updateSalesContact]`)
    }),

    // UPDATE ACCOUNT (SALES CONTACT)
    updateAccount: builder.mutation<UpdateAccountResponse['account'] | null, UpdateAccountPayload>({
      invalidatesTags: [TagTypes.ACCOUNT],
      query: ({ accountId, contactId, account }) => ({
        method: Methods.PATCH,
        url: `/contacts/${contactId}/accounts/${accountId}`,
        body: { account }
      }),
      transformResponse: ({ success, data }: Response<UpdateAccountResponse>) => {
        return success && data ? data.account : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:updateAccount]`)
    }),

    // ADD NEW OPPORTUNITY CASE
    createOpportunityCase: builder.mutation<void, CreateOpportunityCasePayload>({
      invalidatesTags: [TagTypes.CASE],
      query: ({ accountId, caseContent, contactId, opportunityId }) => ({
        method: Methods.POST,
        url: `/contacts/${contactId}/accounts/${accountId}/opportunities/${opportunityId}/cases`,
        body: caseContent
      }),
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:createOpportunityCase]`)
    }),

    // SEND SURVEY CLOSED EVENT
    sendSurveyClosedEvent: builder.mutation<void, SendSurveyClosedPayload>({
      query: ({ surveyType, contactId, accountId, opportunityId }) => ({
        method: Methods.POST,
        url: `/contacts/${contactId}/accounts/${accountId}/opportunities/${opportunityId}/surveys/closed`,
        body: { surveyType }
      }),
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTACTS}:sendSurveyClosedEvent]`)
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // Queries
  useGetContactQuery,
  useGetContactReferralsQuery,
  useGetMyEventsQuery,

  // Mutations
  useCreateOpportunityCaseMutation,
  useUpdateAccountMutation,
  useUpdateOpportunityMutation,
  useUpdateSalesContactMutation,
  useSendSurveyClosedEventMutation
} = contactsBoltApi
